/**
 * API Client para listagem de freelancers em shortlists
 *
 * Todas as chamadas usam o token JWT do Supabase para autenticacao.
 */

import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";

const API_BASE_URL = SUPABASE_URL.replace(/\/$/, "");

function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return apiFetch<T>(`${API_BASE_URL}${endpoint}`, options);
}

// ============================================
// Tipos
// ============================================

export interface ShortlistInfo {
  vaga_id: string;
  agencia: string | null;
  cliente: string | null;
  shortlisted_at: string | null;
}

export interface ShortlistedFreelancer {
  freelancer_id: string;
  nome: string;
  areas: string[] | null;
  pretensao_salarial: string | null;
  shortlists: ShortlistInfo[];
  ultimo_shortlist: string | null;
  total_shortlists: number;
}

export interface PaginationInfo {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface ShortlistedFreelancersResponse {
  data: ShortlistedFreelancer[];
  pagination: PaginationInfo;
}

export interface ShortlistedFreelancersParams {
  page?: number;
  per_page?: number;
  area?: string;
  pretensao?: string;
  agencia?: string;
  order_by?: string;
  order_dir?: "asc" | "desc";
}

// ============================================
// API Calls
// ============================================

export async function getShortlistedFreelancers(
  params: ShortlistedFreelancersParams = {}
): Promise<ShortlistedFreelancersResponse> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set("page", params.page.toString());
  if (params.per_page) searchParams.set("per_page", params.per_page.toString());
  if (params.area) searchParams.set("area", params.area);
  if (params.pretensao) searchParams.set("pretensao", params.pretensao);
  if (params.agencia) searchParams.set("agencia", params.agencia);
  if (params.order_by) searchParams.set("order_by", params.order_by);
  if (params.order_dir) searchParams.set("order_dir", params.order_dir);

  const queryString = searchParams.toString();
  const endpoint = `/recommendations/shortlists/freelancers${queryString ? `?${queryString}` : ""}`;

  return fetchWithAuth<ShortlistedFreelancersResponse>(endpoint);
}
